import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Crosshair, Stop, UserFocus, FloppyDisk, MapPin, Square, ImageSquare } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const btn = "px-3 py-2 text-xs font-mono-data uppercase tracking-wider border transition-colors duration-200 disabled:opacity-40";

function useTrack() {
  const [st, setSt] = useState({ state: "idle", sightings: [] });
  const load = useCallback(() => api.get("/eye/track/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  return [st, load];
}

function Timeline({ rows }) {
  if (!rows.length) return <p className="text-xs font-mono-data text-[#71717A] py-6" data-testid="eye-timeline-empty">No sightings yet.</p>;
  return (
    <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="eye-timeline">
      {rows.map((s) => (
        <div key={s.id} className="flex items-center gap-3 border border-[#27272A] bg-[#0F0F12] p-2" data-testid={`eye-sighting-${s.id}`}>
          {s.snapshot_url ? <img src={BASE + s.snapshot_url} alt="" className="w-24 h-14 object-cover" />
            : <div className="w-24 h-14 bg-[#18181B]" />}
          <div className="text-xs font-mono-data">
            <p className="text-white">{s.camera_label}</p>
            <p className="text-[#A1A1AA]">{new Date(s.ts).toLocaleTimeString()}{s.sms ? ` · SMS ${s.sms}` : ""}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function TrackPanel({ st, reload, cams }) {
  const [name, setName] = useState("Salman");
  const [camId, setCamId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!camId && cams.length) setCamId((cams.find((c) => c.label === "punch_in") || cams[0]).id); }, [cams, camId]);
  const live = st.state === "armed" || st.state === "tracking";
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } setBusy(false); };
  const color = { armed: "#EAB308", tracking: "#22C55E" }[st.state] || "#71717A";
  return (
    <div className="border border-[#27272A] bg-[#0A0A0C] p-5 space-y-4" data-testid="eye-track-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-bold tracking-tight">Track {name || "person"}</h2>
        <span className="text-xs font-mono-data uppercase px-2 py-1 border" style={{ color, borderColor: color }} data-testid="eye-track-state">{st.state}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#18181B] border border-[#27272A] px-3 py-2 text-sm text-white" data-testid="eye-track-name-input" />
        <button className={`${btn} border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10`} disabled={busy || live || !name.trim()} data-testid="eye-track-arm-btn"
          onClick={() => run(() => api.post("/eye/track/start", { name: name.trim() }), "Armed — waiting at Punch-in")}><Crosshair className="inline mr-1" />Arm</button>
        <button className={`${btn} border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10`} disabled={busy || !live} data-testid="eye-track-stop-btn"
          onClick={() => run(() => api.post("/eye/track/stop"), "Stopped")}><Stop className="inline mr-1" />Stop</button>
      </div>
      <p className="text-xs font-mono-data text-[#A1A1AA]" data-testid="eye-track-last-camera">Last camera: {st.last_camera || "—"}</p>
      <div className="flex flex-wrap gap-2 items-center border-t border-[#27272A] pt-3">
        <select value={camId} onChange={(e) => setCamId(e.target.value)} className="bg-[#18181B] border border-[#27272A] px-2 py-2 text-xs text-white" data-testid="eye-enroll-camera-select">
          {cams.map((c) => <option key={c.id} value={c.id}>{c.name}{c.label ? ` [${c.label}]` : ""}</option>)}
        </select>
        <button className={`${btn} border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10`} disabled={busy || !camId || !name.trim()} data-testid="eye-enroll-btn"
          onClick={() => run(() => api.post("/eye/enroll", { name: name.trim(), camera_id: camId }, { timeout: 60000 }), "5 reference frames saved")}><UserFocus className="inline mr-1" />Enroll {name}</button>
        <button className={`${btn} border-[#A1A1AA] text-[#A1A1AA] hover:bg-white/5`} disabled={busy || !camId} data-testid="eye-label-punch-btn"
          onClick={() => run(() => api.post(`/eye/cameras/${camId}/label`, { label: "punch_in" }), "Labeled punch_in")}>Set as punch_in</button>
      </div>
      <Timeline rows={st.sightings || []} />
    </div>
  );
}

function MapCanvas({ map, setMap, mode, cams, camSel, st }) {
  const box = useRef(null);
  const [draft, setDraft] = useState(null);
  const drag = useRef(null);
  const pt = (e) => { const r = box.current.getBoundingClientRect(); return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) }; };
  const camName = (id) => (cams.find((c) => c.id === id) || {}).name || "";
  const onDown = (e) => {
    const p = pt(e);
    if (mode === "marker") {
      const name = window.prompt("Marker name (e.g. punch_in, aisle_1):", (cams.find((c) => c.id === camSel) || {}).label || "");
      if (name) setMap((m) => ({ ...m, markers: [...m.markers, { n: m.markers.length + 1, name, camera_id: camSel, x: p.x, y: p.y }] }));
    } else if (mode === "zone") setDraft({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const onMove = (e) => {
    const p = pt(e);
    if (drag.current != null) setMap((m) => ({ ...m, markers: m.markers.map((k, i) => (i === drag.current ? { ...k, x: p.x, y: p.y } : k)) }));
    else if (draft) setDraft((d) => ({ ...d, x2: p.x, y2: p.y }));
  };
  const onUp = () => {
    drag.current = null;
    if (!draft) return;
    const z = { x: Math.min(draft.x1, draft.x2), y: Math.min(draft.y1, draft.y2), w: Math.abs(draft.x2 - draft.x1), h: Math.abs(draft.y2 - draft.y1) };
    setDraft(null);
    if (z.w < 0.03 || z.h < 0.03) return;
    const label = window.prompt("Zone label:", "");
    if (label) setMap((m) => ({ ...m, zones: [...m.zones, { ...z, label, camera_id: camSel }] }));
  };
  const lastCam = st.last_camera_id;
  const path = [...(st.sightings || [])].slice(0, 20).reverse()
    .map((s) => map.markers.find((k) => k.camera_id === s.camera_id)).filter(Boolean);
  const token = localStorage.getItem("sv_token");
  const bg = map.bg_preview || (map.bg_path ? `${API}/events/media/${map.bg_path}?auth=${token}` : null);
  return (
    <div ref={box} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} data-testid="eye-map-canvas"
      className="relative w-full aspect-[16/9] border border-[#27272A] bg-[#0F0F12] select-none overflow-hidden"
      style={{ backgroundImage: bg ? `url(${bg})` : undefined, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center", cursor: mode === "move" ? "default" : "crosshair" }}>
      {map.zones.map((z, i) => {
        const hot = lastCam && z.camera_id === lastCam;
        return <div key={i} data-testid={`eye-map-zone-${i}`} className="absolute border-2 text-[10px] font-mono-data p-1"
          style={{ left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.w * 100}%`, height: `${z.h * 100}%`, borderColor: hot ? "#22C55E" : "#3B82F6", background: hot ? "rgba(34,197,94,0.25)" : "rgba(59,130,246,0.08)", color: "#fff" }}>{z.label}</div>;
      })}
      {draft && <div className="absolute border-2 border-dashed border-[#EAB308]" style={{ left: `${Math.min(draft.x1, draft.x2) * 100}%`, top: `${Math.min(draft.y1, draft.y2) * 100}%`, width: `${Math.abs(draft.x2 - draft.x1) * 100}%`, height: `${Math.abs(draft.y2 - draft.y1) * 100}%` }} />}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        {path.length > 1 && <polyline data-testid="eye-map-path" points={path.map((k) => `${k.x * 100},${k.y * 100}`).join(" ")} fill="none" stroke="#22C55E" strokeWidth="0.5" strokeDasharray="1.5 1" />}
      </svg>
      {map.markers.map((k, i) => {
        const hot = lastCam && k.camera_id === lastCam;
        return (
          <div key={i} data-testid={`eye-map-marker-${i}`} title={camName(k.camera_id)}
            onMouseDown={(e) => { if (mode === "move") { e.stopPropagation(); drag.current = i; } }}
            className={`absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 ${mode === "move" ? "cursor-move" : ""}`} style={{ left: `${k.x * 100}%`, top: `${k.y * 100}%` }}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${hot ? "bg-[#22C55E] text-black animate-pulse ring-4 ring-[#22C55E]/40" : "bg-[#3B82F6] text-white"}`}>{k.n}</span>
            <span className="text-[10px] font-mono-data text-white bg-black/70 px-1">{k.name}</span>
          </div>
        );
      })}
    </div>
  );
}

function MapEditor({ cams, st }) {
  const [map, setMap] = useState({ markers: [], zones: [] });
  const [mode, setMode] = useState("move");
  const [camSel, setCamSel] = useState("");
  const [bgB64, setBgB64] = useState(null);
  const load = () => api.get("/eye/map").then((r) => setMap({ markers: [], zones: [], ...r.data })).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!camSel && cams.length) setCamSel(cams[0].id); }, [cams, camSel]);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader();
    rd.onload = () => { setBgB64(rd.result); setMap((m) => ({ ...m, bg_preview: rd.result })); };
    rd.readAsDataURL(f);
  };
  const save = async () => {
    try {
      const r = await api.post("/eye/map", { markers: map.markers, zones: map.zones, background_b64: bgB64 });
      setBgB64(null); setMap({ ...r.data }); toast.success("Store map saved");
    } catch { toast.error("Save failed"); }
  };
  const mb = (m, Icon, label) => (
    <button className={`${btn} ${mode === m ? "border-[#EAB308] text-[#EAB308]" : "border-[#27272A] text-[#A1A1AA]"}`} onClick={() => setMode(m)} data-testid={`eye-map-mode-${m}`}><Icon className="inline mr-1" />{label}</button>
  );
  return (
    <div className="border border-[#27272A] bg-[#0A0A0C] p-5 space-y-3" data-testid="eye-map-editor">
      <div className="flex flex-wrap gap-2 items-center">
        {mb("move", MapPin, "Move")}{mb("marker", MapPin, "Add camera marker")}{mb("zone", Square, "Draw zone")}
        <select value={camSel} onChange={(e) => setCamSel(e.target.value)} className="bg-[#18181B] border border-[#27272A] px-2 py-2 text-xs text-white" data-testid="eye-map-camera-select">
          {cams.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className={`${btn} border-[#27272A] text-[#A1A1AA] cursor-pointer`} data-testid="eye-map-bg-upload"><ImageSquare className="inline mr-1" />Floor plan<input type="file" accept="image/*" className="hidden" onChange={onFile} /></label>
        <button className={`${btn} border-[#27272A] text-[#A1A1AA]`} onClick={() => setMap((m) => ({ ...m, markers: [], zones: [] }))} data-testid="eye-map-clear-btn">Clear</button>
        <button className={`${btn} border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10`} onClick={save} data-testid="eye-map-save-btn"><FloppyDisk className="inline mr-1" />Save</button>
        <button className={`${btn} border-[#27272A] text-[#A1A1AA]`} onClick={load} data-testid="eye-map-load-btn">Load</button>
      </div>
      <p className="text-[11px] font-mono-data text-[#71717A]">Pick a camera, then click to place its marker or drag to draw a zone. The marker/zone of the latest sighting glows green; the dashed line is the last 20 sightings.</p>
      <MapCanvas map={map} setMap={setMap} mode={mode} cams={cams} camSel={camSel} st={st} />
    </div>
  );
}

export default function Eye() {
  const [st, reload] = useTrack();
  const [cams, setCams] = useState([]);
  const [tab, setTab] = useState("track");
  useEffect(() => { api.get("/eye/cameras").then((r) => setCams(r.data.cameras || [])).catch(() => {}); }, []);
  const tb = (k, label) => (
    <button onClick={() => setTab(k)} data-testid={`eye-tab-${k}`} className={`${btn} ${tab === k ? "border-[#3B82F6] text-white bg-[#3B82F6]/15" : "border-[#27272A] text-[#A1A1AA]"}`}>{label}</button>
  );
  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="eye-page">
      <div className="flex gap-2">{tb("track", "Track person")}{tb("map", "Store Map")}</div>
      {tab === "track" ? <TrackPanel st={st} reload={reload} cams={cams} /> : <MapEditor cams={cams} st={st} />}
    </div>
  );
}
